import React from 'react';
import HeroSection from '../components/common/HeroSection';
import SectionTitle from '../components/common/SectionTitle';

const Practices = () => {
  const practices = [
    {
      title: "Agile Development",
      description: "Our Agile development methodology ensures flexibility, collaboration, and faster delivery of high-quality software products.",
      points: [
        "Iterative and incremental approach to software development",
        "Regular client feedback and collaboration",
        "Adaptability to changing requirements",
        "Continuous delivery of functional software"
      ],
      icon: (
        <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
        </svg>
      )
    },
    {
      title: "DevOps",
      description: "Our DevOps practices foster collaboration between development and operations teams to streamline software delivery.",
      points: [
        "Continuous Integration and Continuous Deployment (CI/CD)",
        "Automated testing and deployment",
        "Infrastructure as Code (IaC)",
        "Monitoring and logging for performance optimization"
      ],
      icon: (
        <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path fillRule="evenodd" d="M2 5a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V5zm3.293 1.293a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 01-1.414-1.414L7.586 10 5.293 7.707a1 1 0 010-1.414zM11 12a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
        </svg>
      )
    },
    {
      title: "Quality Assurance",
      description: "We implement comprehensive quality assurance practices to ensure the reliability and performance of our software solutions.",
      points: [
        "Automated and manual testing strategies",
        "Performance and security testing",
        "Code reviews and static code analysis",
        "User acceptance testing (UAT)"
      ],
      icon: (
        <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      )
    },
    {
      title: "User-Centered Design",
      description: "We prioritize user experience through user-centered design practices, ensuring intuitive and engaging software solutions.",
      points: [
        "User research and persona development",
        "Wireframing and prototyping",
        "Usability testing",
        "Iterative design based on user feedback"
      ],
      icon: (
        <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
        </svg>
      )
    },
    {
      title: "Security-First Development",
      description: "We implement security best practices throughout the development lifecycle to protect your data and applications.",
      points: [
        "Secure coding practices",
        "Regular security assessments",
        "Compliance with industry standards",
        "Data protection and privacy by design"
      ],
      icon: (
        <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
        </svg>
      )
    },
    {
      title: "Scalable Architecture",
      description: "We design scalable architectures that can grow with your business and handle increasing demands.",
      points: [
        "Microservices architecture",
        "Cloud-native development",
        "Horizontal and vertical scaling strategies",
        "Performance optimization"
      ],
      icon: (
        <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM11 13a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      )
    }
  ];

  return (
    <div className="practices-page">
      <HeroSection 
        title="Our Practices"
        breadcrumb={[{ name: 'Practices' }]}
        background="/assets/images/practices-bg.jpg"
      />
      
      <section className="py-16">
        <div className="container mx-auto px-4">
          <SectionTitle 
            subtitle="Best Practices"
            title="Our Approach to Software Development"
            className="mb-16"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {practices.map((practice, index) => (
              <div 
                key={index} 
                className="bg-white p-8 rounded-lg shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="text-blue-600 mb-6">
                  {practice.icon}
                </div>
                
                <h3 className="text-xl font-bold text-gray-800 mb-4">{practice.title}</h3>
                <p className="text-gray-600 mb-6">{practice.description}</p>
                
                <ul className="space-y-2">
                  {practice.points.map((point, pointIndex) => (
                    <li key={pointIndex} className="flex items-start">
                      <div className="mr-3 mt-1 text-blue-600">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span className="text-gray-600 text-sm">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          
          <div className="mt-20 text-center">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Need a Partner Who Follows Best Practices?</h3>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
              We are committed to delivering high-quality software solutions through our proven development practices.
              Let's discuss how our approach can benefit your next project.
            </p>
            <a 
              href="/contact-us" 
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded inline-block transition-colors"
            >
              Get in Touch
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Practices;